import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '@env/environment';
import { EnrollmentGroup, Site, SiteAvailableEnrollmentGroupRow } from '@shared/models';
import { deserialize, serialize } from '@shared/models/base-helper';
import { DocusignTemplate } from '@shared/models/docusign-template.model';

interface QueryResponse {
  results: any[];
  total: number;
}

interface SiteAvailableEnrollmentGroupDashboardResponse {
  results: SiteAvailableEnrollmentGroupRow[];
  total: number;
}

interface DeserializedResponse {
  results: EnrollmentGroup[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class EnrollmentGroupService {
  apiUrl = environment.apiUrl + '/enrollment_groups/';
  templateApiUrl = environment.apiUrl + '/docusign_templates/';

  constructor(readonly http: HttpClient) {}

  getAllEnrollmentGroups(): Observable<EnrollmentGroup[]> {
    return this.getFilteredEnrollmentGroups('').pipe(map(response => response.results));
  }

  getEnrollmentGroup(id: number): Observable<EnrollmentGroup> {
    let params = new HttpParams();
    return this.http
      .get<any>(`${this.apiUrl}${id}`, { params })
      .pipe(map(group => (group ? deserialize(group, EnrollmentGroup) : null)));
  }

  getFilteredEnrollmentGroups(filterString: string): Observable<DeserializedResponse> {
    const filterUrl = this.apiUrl + filterString;
    return this.http.get<QueryResponse>(filterUrl).pipe(
      map(response => {
        const deserializedResponse: DeserializedResponse = {
          results: [],
          total: response.total,
        };
        for (const group of response.results) {
          deserializedResponse.results.push(deserialize(group, EnrollmentGroup));
        }
        return deserializedResponse;
      })
    );
  }

  getEnrollmentGroupsByLatLong(lat: number = null, lng: number = null): Observable<EnrollmentGroup[]> {
    let params = new HttpParams();

    if (lat && lng) {
      params = params.append('lat', lat.toString());
      params = params.append('lng', lng.toString());
    }

    return this.http
      .get<any[]>(`${this.apiUrl}lat-long`, { params })
      .pipe(map(groups => groups.map((group: any) => deserialize(group, EnrollmentGroup))));
  }

  createEnrollmentGroup(group: any, originalEnrollmentGroupId: number): Observable<EnrollmentGroup> {
    let params = ''
    if (originalEnrollmentGroupId) {
      params = 'original_enrollment_group_id=' + originalEnrollmentGroupId;
    }
    return this.http.post<any>(`${this.apiUrl}?${params}`, group)
      .pipe(map((respEnrollmentGroup: any) => deserialize(respEnrollmentGroup, EnrollmentGroup)));
  }

  updateEnrollmentGroup(enrollmentGroup: any): Observable<EnrollmentGroup> {
    return this.http
      .put<any>(`${this.apiUrl}${enrollmentGroup.id}`, enrollmentGroup)
      .pipe(map((respEnrollmentGroup: any) => deserialize(respEnrollmentGroup, EnrollmentGroup)));
  }

  getEdocTemplates() {
    return this.http
      .get<any>(`${this.templateApiUrl}`)
      .pipe(map((templates: any[]) => templates.map(template => deserialize(template, DocusignTemplate))));
  }

  addUpdateDocusignTemplate(template: DocusignTemplate) {
    return this.http.post(`${this.templateApiUrl}`, serialize(template));
  }

  deleteDocusignTemplate(id: number) {
    return this.http.delete(`${this.templateApiUrl}/${id}`);
  }

  updateCustomEnrollmentGroupForSite(siteId: number, enrollmentGroup: EnrollmentGroup): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}site/${siteId}/custom_enrollment_group`, enrollmentGroup)
      .pipe(map((respSite: any) => deserialize(respSite, Site)));
  }

  deleteCustomEnrollmentGroupForSite(siteId: number): Observable<any> {
    return this.http
      .delete<any>(`${this.apiUrl}site/${siteId}/custom_enrollment_group`)
      .pipe(map((respSite: any) => deserialize(respSite, Site)));
  }

  getFilteredSiteAvailableEnrollmentGroups(filterString: string): Observable<SiteAvailableEnrollmentGroupDashboardResponse> {
    const filterUrl = environment.apiUrl + '/sites/available_enrollment_groups/' + filterString;
    return this.http.get<any>(filterUrl).pipe(
      map(response => ({
        results: response.results.map((row: any) => deserialize(row, SiteAvailableEnrollmentGroupRow)),
        total: response.total,
      }))
    );
  }
}
